import CryptoJS from 'crypto-js';

export type LocalEncryptedMediaBlob = {
  algorithm: 'AES-256-CBC';
  ciphertextBase64: string;
  ivBase64: string;
  wrappedContentKeyBase64: string;
  sha256CiphertextHex: string;
};

function wordArrayToUint8Array(wordArray: CryptoJS.lib.WordArray): Uint8Array {
  const words = wordArray.words;
  const sigBytes = wordArray.sigBytes;
  const out = new Uint8Array(sigBytes);

  for (let i = 0; i < sigBytes; i++) {
    out[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
  }
  return out;
}

function uint8ArrayToWordArray(arr: Uint8Array): CryptoJS.lib.WordArray {
  const words: number[] = [];
  for (let i = 0; i < arr.length; i++) {
    const idx = i >>> 2;
    const prev = words[idx] || 0;
    words[idx] = prev | (arr[i] << (24 - (i % 4) * 8));
  }
  return CryptoJS.lib.WordArray.create(words, arr.length);
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(String(b64 || ''));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToB64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

export function encryptMediaBase64(plainBase64: string): LocalEncryptedMediaBlob {
  const plainBytes = b64ToBytes(plainBase64);
  if (!plainBytes.length) throw new Error('empty_plain_media_bytes');

  const key = CryptoJS.lib.WordArray.random(32);
  const iv = CryptoJS.lib.WordArray.random(16);
  const plainWA = uint8ArrayToWordArray(plainBytes);

  const encrypted = CryptoJS.AES.encrypt(plainWA, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  const cipherWA = encrypted.ciphertext;
  const sha256CiphertextHex = CryptoJS.SHA256(cipherWA).toString(CryptoJS.enc.Hex);

  return {
    algorithm: 'AES-256-CBC',
    ciphertextBase64: CryptoJS.enc.Base64.stringify(cipherWA),
    ivBase64: CryptoJS.enc.Base64.stringify(iv),
    wrappedContentKeyBase64: CryptoJS.enc.Base64.stringify(key),
    sha256CiphertextHex,
  };
}

export function decryptMediaToBase64(enc: {
  ciphertextBase64: string;
  ivBase64: string;
  wrappedContentKeyBase64: string;
}): string {
  const ciphertext = CryptoJS.enc.Base64.parse(String(enc.ciphertextBase64 || ''));
  const iv = CryptoJS.enc.Base64.parse(String(enc.ivBase64 || ''));
  const key = CryptoJS.enc.Base64.parse(String(enc.wrappedContentKeyBase64 || ''));

  const decrypted = CryptoJS.AES.decrypt(
    { ciphertext } as any,
    key,
    {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  );

  const bytes = wordArrayToUint8Array(decrypted);
  return bytesToB64(bytes);
}


export function decryptMediaBase64WithContentKey(enc: {
  ciphertextBase64: string;
  ivBase64: string;
  contentKeyBase64: string;
}): string {
  const ciphertext = CryptoJS.enc.Base64.parse(String(enc.ciphertextBase64 || ''));
  const iv = CryptoJS.enc.Base64.parse(String(enc.ivBase64 || ''));
  const key = CryptoJS.enc.Base64.parse(String(enc.contentKeyBase64 || ''));

  const decrypted = CryptoJS.AES.decrypt(
    { ciphertext } as any,
    key,
    {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  );

  const bytes = wordArrayToUint8Array(decrypted);
  return bytesToB64(bytes);
}
